import React, { useContext, useState } from "react";
import { useAuthQuery as useQuery } from '../../../shared/hooks/useAuthQuery';
import { useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import { useConvexAuth } from "convex/react";
import { AdminRoutes } from "../../../shared/models";
import { api } from "../../../convex/_generated/api";
import { Button } from "../../../catalyst-components/button";
import { Textarea } from "../../../catalyst-components/textarea";
import { Dialog, DialogTitle } from "../../../catalyst-components/dialog";
import { parseMarkdown } from "../../../shared/helpers/markdown";
import { BaseContext } from "../../../shared/base-context";
import { Id } from "../../../convex/_generated/dataModel";

export const ManualContextUpload: React.FC = () => {
    const [text, setText] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate();
    const { isAuthenticated } = useConvexAuth();
    const {convexUserId} = useContext(BaseContext)

    const uploadContext = useMutation(api.manual_global_context_uploads.uploadAndEmbed);
    const entries = useQuery(api.manual_global_context_uploads.getAll);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!text.trim()) {
            setError("Please enter some text to upload");
            return;
        }

        setIsUploading(true);
        setError(null);
        setSuccess(null);

        try {
            await uploadContext({ text, user_id:  convexUserId as Id<"users">});
            setSuccess("Text uploaded and embedded successfully!");
            setText("");
            setIsModalOpen(false);
        } catch (err) {
            setError("Failed to upload text. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="min-h-screen bg-background pt-24 p-4">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <Button color="light" onClick={() => navigate(AdminRoutes.Context)}>
                        Back to Context Management
                    </Button>
                    <h1 className="text-2xl font-bold">Manual Context Uploads</h1>
                </div>
                <Button color="light" onClick={() => setIsModalOpen(true)}>
                    Upload New Context
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {entries?.map((entry) => (
                    <div 
                        key={entry._id} 
                        className="border rounded-lg p-4 bg-white shadow hover:shadow-md transition-shadow"
                    >
                        <p className="text-sm text-gray-600 mb-2">
                            {new Date(entry._creationTime).toLocaleString()}
                        </p>
                        <div 
                            className="text-gray-800 break-words"
                            dangerouslySetInnerHTML={{
                                __html: parseMarkdown(entry.text)
                            }}
                        />
                    </div>
                ))}
            </div>

            <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <DialogTitle>Upload New Context</DialogTitle>
                <form onSubmit={handleSubmit} className="space-y-4 p-4">
                    <div>
                        <label htmlFor="text" className="block text-sm font-medium text-gray-700 mb-2">
                            Enter text to upload
                        </label>
                        <Textarea
                            id="text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={10}
                            className="w-full"
                            placeholder="Paste your text here..."
                        />
                    </div>

                    {error && (
                        <div className="text-red-600 bg-red-50 p-3 rounded-md">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="text-green-600 bg-green-50 p-3 rounded-md">
                            {success}
                        </div>
                    )}

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            color="light"
                            onClick={() => setIsModalOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color="light"
                            disabled={isUploading || !text.trim()}
                        >
                            {isUploading ? "Uploading..." : "Upload and Embed"}
                        </Button>
                    </div>
                </form>
            </Dialog>
        </div>
    );
}; 